/**
 * APEX v8.7 — Champion Archetypes
 *
 * Base de connaissance statique par champion et par rôle.
 * Sources : Data Dragon stats, connaissance méta EUW Master+ (patch 26.x)
 *
 * Archétypes :
 *   hypercarry   — Scale fort, fragile early, win = survivre et farmer
 *   lane_bully   — Domine niveau 1-6, snowball lead
 *   poke         — Harcèle de loin, gagne les trades longue portée
 *   skirmisher   — Bon en trades courts, mobile, joue les all-in
 *   utility      — Apporte CC ou peel, win = teamfight
 *   siege        — Push de tourelle, wave clear, objectifs
 *
 * Structure par champion :
 *   archetype       : string         — archétype DOMINANT (définit la win condition centrale)
 *   subArchetypes   : string[]       — dimensions secondaires (optionnel)
 *   winCondition    : string
 *   powerSpike      : string
 *   keyMistakes     : string[]
 *   axisAdvice      : { cs_per_min, kda, lane_wr_15, damage_per_min, vision, deaths_per_game, win_rate }
 *   supportSynergy  : string[]
 *   difficulty      : 'low' | 'medium' | 'high'
 */

import { MONTH_MS } from '../constants';

export const ARCHETYPE_LABELS = {
  hypercarry: 'Hypercarry',
  lane_bully: 'Lane Bully',
  poke:       'Poke',
  skirmisher: 'Skirmisher',
  utility:    'Utility',
  siege:      'Siege',
};

export const CHAMPION_ARCHETYPES = {

  ADC: {

    Jinx: {
      archetype: 'hypercarry',
      subArchetypes: ['siege'],
      tags: [
        { label: 'Snowball teamfight', type: 'strength' },
        { label: 'Scale fort', type: 'strength' },
        { label: 'Reset (P) = carry', type: 'strength' },
        { label: 'Fragile early', type: 'caution' },
        { label: 'Besoin peel', type: 'caution' },
      ],
      winCondition: "Survivre l'early, obtenir un reset (P) sur une kill/objectif, puis tout détruire en mid/late. Tu n'as pas besoin de gagner la lane — tu as besoin de ne pas mourir.",
      powerSpike: "2 objets (Kraken + Runaan's). Avant : vulnérable. Après : ingérable. Reset (P) en fait une machine à snowball les teamfights.",
      keyMistakes: [
        "Overextend en lane pour un kill non nécessaire",
        "Rester en Q-form sur cible isolée au lieu de W pour kiter",
        "Ne pas profiter du reset (P) pour changer de cible",
        "Mourir sur une wave alors que l'ennemi a le prio"
      ],
      axisAdvice: {
        cs_per_min: "Hypercarry = le farm est ta priorité absolue. Chaque CS raté retarde ton spike. Vise 8+ cs/min.",
        kda: "KDA <2.5 sur Jinx = tu meurs avant de carry. Joue derrière ton support en early.",
        lane_wr_15: "Gagner la lane n'est pas la priorité. 45-55% Lane WR acceptable, l'objectif est de ne pas perdre trop de farm.",
        damage_per_min: "Damage faible = passive (P) pas assez exploitée. Après un kill, reset et continue d'attaquer.",
        vision: "Wards côté flanc pour anticiper les dives — tu es cible prioritaire.",
        deaths_per_game: ">4 morts critique. Si la carry meurt, le team perd son win condition.",
        win_rate: "WR <50% (15+ games) = soit tu meurs trop tôt, soit pas les 2 objets au mid-game."
      },
      supportSynergy: ['enchanteuse', 'bouclier', 'engage_dur'],
      difficulty: 'low'
    },

    "Kai'Sa": {
      archetype: 'skirmisher',
      tags: [
        { label: 'Assassine carry', type: 'strength' },
        { label: 'Burst isolé', type: 'strength' },
        { label: 'Flexible AP/AD', type: 'strength' },
        { label: 'Besoin engage', type: 'caution' },
        { label: 'Skill-dépendant', type: 'caution' },
      ],
      winCondition: "Évoluer W en early, identifier un carry isolé, l'éliminer avec E-W-R. Tu gagnes en ciblant le bon ennemi, pas en auto-attaquant en ligne.",
      powerSpike: "Evolution W + Kraken/Guinsoo. Niveau 11 (R amélioré).",
      keyMistakes: [
        "Utiliser R pour fuir au lieu d'engage sur cible isolée",
        "Mal stacker P (priorité carry ennemi)",
        "Full AP sans evolution Q — perd le burst single target",
        "Rester backline au lieu de plonger avec R"
      ],
      axisAdvice: {
        cs_per_min: "Besoin de gold pour évoluer. 7+ cs/min minimum. Evolution retardée = spike retardé.",
        kda: "KDA <2 = mauvaises cibles ou sur-dive. L'assassination ciblée donne le bon KDA.",
        lane_wr_15: "Bonne en lane avec engage support. Vise 50%+.",
        damage_per_min: "Damage faible = trop en backline. Kai'Sa doit plonger.",
        vision: "Vision latérale critique pour sécuriser les dives R.",
        deaths_per_game: "3-4 acceptable si trades positifs. Au-delà = sur-dive ou R sur cible non-isolée.",
        win_rate: "WR faible : R en engage ou en fuite ? L'engage proactif est la win condition."
      },
      supportSynergy: ['engage_dur', 'cc_long', 'nautilus', 'leona'],
      difficulty: 'medium'
    },

    Caitlyn: {
      archetype: 'poke',
      subArchetypes: ['lane_bully', 'siege'],
      tags: [
        { label: 'Domine la lane', type: 'strength' },
        { label: 'Zone control', type: 'strength' },
        { label: 'Siege tour', type: 'strength' },
        { label: 'Faible en late', type: 'caution' },
        { label: 'Q immobile', type: 'caution' },
      ],
      winCondition: "Dominer la lane avec la portée et les trappes, transformer le lead en or et en sieges de tourelle. Tu gagnes en zone control, pas en all-in.",
      powerSpike: "Niveau 2 (Q+W setup), premier objet. Fort early/mid, tombe en late vs hypercarries.",
      keyMistakes: [
        "Ne pas placer les trappes (W) sur les bushes pour zoner",
        "Headshots aléatoires au lieu de la cible prioritaire",
        "Immobile pendant le cast de Q — punishable",
        "Ne pas abuser de la portée en early"
      ],
      axisAdvice: {
        cs_per_min: "650 de portée = aucune excuse pour rater du farm. 8+ cs/min. Les rates sous tour sont impardonnables.",
        kda: "KDA <3 = tu te fais catch hors position ou tu chase trop.",
        lane_wr_15: "Tu devrais gagner la lane. <45% = problème de placement ou de gestion du wave.",
        damage_per_min: "Stack les headshots : W-auto sur cibles sous trappes.",
        vision: "Tes trappes (W) complètent les wards. Place-les côté jungler ennemi.",
        deaths_per_game: ">4 morts = catch ou trop longtemps en lane hors vision.",
        win_rate: "WR faible en mid-late : identifie le moment pour push/roam avant que tu deviennes faible."
      },
      supportSynergy: ['poke', 'zone_control', 'lulu', 'karma', 'xerath'],
      difficulty: 'low'
    },

    Jhin: {
      archetype: 'poke',
      subArchetypes: ['utility'],
      tags: [
        { label: 'Sniper mid-game', type: 'strength' },
        { label: 'Win teamfight R', type: 'strength' },
        { label: 'Fragile early', type: 'caution' },
        { label: 'Besoin peel', type: 'caution' },
        { label: 'Faible vs hypercarry', type: 'caution' },
      ],
      winCondition: "Contrôler l'espace avec W et les crit-autos, accumuler or, win les teamfights à distance avec R. Tu es un sniper qui place chaque balle.",
      powerSpike: "Galeforce + Infinity Edge. Niveau 6 (R). Fort en mid-game teamfight.",
      keyMistakes: [
        "Gaspiller la 4ème balle (crit) sur un minion",
        "W quand l'ennemi bouge au lieu d'anticiper",
        "Placer R depuis une position non-sécurisée",
        "Rester à 0/4 balles en fight"
      ],
      axisAdvice: {
        cs_per_min: "Jhin farm avec la 4ème balle de loin. 7.5+ cs/min. Waves mal gérées = power spike raté.",
        kda: "KDA >3 impératif — fragile et aucune mobilité. Chaque mort = wave + recalls.",
        lane_wr_15: "Fort en lane avec bon support. 50%+. Abuse de W avant les all-ins.",
        damage_per_min: "Faible = R sur cibles non-immobilisées ou balles gaspillées sur minions.",
        vision: "Critique — aucune escape. Ward côté jungler ennemi systématiquement.",
        deaths_per_game: "Max 3. Au-delà = positionnement trop avancé ou R mal placé.",
        win_rate: "WR faible : tu win la lane puis perds le mid-game ? Jhin tombe vs hypercarries en late."
      },
      supportSynergy: ['cc_long', 'engage_dur', 'zyra', 'senna', 'thresh'],
      difficulty: 'medium'
    },

    Lucian: {
      archetype: 'lane_bully',
      subArchetypes: ['skirmisher'],
      tags: [
        { label: 'Lane bully', type: 'strength' },
        { label: 'Burst combo', type: 'strength' },
        { label: 'Fort mid-game', type: 'strength' },
        { label: 'Faible si late', type: 'caution' },
        { label: 'Skill-dépendant', type: 'caution' },
      ],
      winCondition: "Écraser la lane niveau 2-9, transformer le lead en kills/objectifs, maintenir la pression mid-game. Si tu atteins le late à égalité, tu as perdu ta fenêtre.",
      powerSpike: "Niveau 2 (Q+E), Kraken Slayer, niveau 11. Fenêtre d'or : level 2 à 25 min.",
      keyMistakes: [
        "Ne pas all-in niveau 2 avec Q+E",
        "Utiliser E pour fuir systématiquement au lieu de trade",
        "Jouer Lucian comme un hypercarry en late",
        "Ne pas alterner autos entre les skills (passive P)"
      ],
      axisAdvice: {
        cs_per_min: "7.5+ cs/min mais secondaire si tu peux kill. Lucian préfère le lead en kills qu'en CS.",
        kda: "KDA 2-3 acceptable (fights agressifs). KDA >4 = tu joues trop passif pour un Lucian.",
        lane_wr_15: ">55% impératif. Si tu ne domines pas la lane, tu rates ta win condition.",
        damage_per_min: "Active la passive (P) entre chaque skill. Combo Q-auto-E-auto-R-auto.",
        vision: "Ward offensif (côté ennemi) pour étendre la pression.",
        deaths_per_game: "3-4 acceptable en lane si trades positifs. Mid-game : <3.",
        win_rate: "WR faible (20+ games) : tu forces les fights mid-game ? Lucian perd si >30 min."
      },
      supportSynergy: ['poke', 'enchanteuse', 'nami', 'karma', 'braum'],
      difficulty: 'medium'
    },

    Draven: {
      archetype: 'lane_bully',
      tags: [
        { label: 'Meilleur early', type: 'strength' },
        { label: 'Gold massif (P)', type: 'strength' },
        { label: 'Snowball brutal', type: 'strength' },
        { label: 'Perd si late', type: 'caution' },
        { label: 'Très technique', type: 'caution' },
      ],
      winCondition: "Snowball dès le niveau 1 avec les axes (Q), convertir chaque kill en gold massif (P), finir avant le late. Le meilleur early carry du jeu.",
      powerSpike: "Niveau 1-2 (axes), Kraken Slayer. Fenêtre : 0-25 min.",
      keyMistakes: [
        "Laisser tomber un axe inutilement",
        "All-in 2 ennemis full vie sans engage support",
        "Mal dépenser les stacks (P)",
        "Jouer agressif après avoir perdu les stacks (P reset à la mort)"
      ],
      axisAdvice: {
        cs_per_min: "7+ cs/min mais Draven préfère les kills. Ne rate pas de farm pour autant.",
        kda: "KDA >2.5. Mourir pour un kill = OK. Mourir pour du farm = pas OK.",
        lane_wr_15: ">60% attendu. Si tu ne domines pas, tu perds ta win condition.",
        damage_per_min: "Faible = tu ne catch pas tes axes ou tu joues trop en arrière.",
        vision: "Ward côté jungler ennemi pour continuer à jouer agressif.",
        deaths_per_game: "Max 3. Chaque mort reset les stacks (P) et donne un gros bounty.",
        win_rate: "WR <50% = stacks perdus inutilement ou jeu trop long."
      },
      supportSynergy: ['engage_dur', 'cc_long', 'leona', 'nautilus', 'thresh'],
      difficulty: 'high'
    },

    Ezreal: {
      archetype: 'poke',
      tags: [
        { label: 'Insaisissable (E)', type: 'strength' },
        { label: 'Scale infini', type: 'strength' },
        { label: 'Safe lane', type: 'strength' },
        { label: 'Skillshot pur', type: 'caution' },
        { label: 'DPS faible', type: 'caution' },
      ],
      winCondition: "Harceler de loin avec Q (stacks Tear), éviter les all-ins avec E, win les mid-game en zone. Tu gagnes en étant insaisissable.",
      powerSpike: "Tear stacké + Trinity + Manamune. Niveau 13 (Manamune active).",
      keyMistakes: [
        "Utiliser E offensivement au lieu de le garder pour l'escape",
        "Viser Q sur le tank au lieu du carry",
        "Ne pas stacker Tear — chaque Q doit toucher",
        "Utiliser R comme poke au lieu de finisher/wave clear"
      ],
      axisAdvice: {
        cs_per_min: "7.5+ cs/min. Ezreal farm mal sous tour — gère les waves proprement.",
        kda: "KDA >3 naturel grâce à E. <2.5 = catch ou over-engage.",
        lane_wr_15: "45-55% acceptable. Ezreal n'est pas un lane bully — stack Tear et évite les pertes.",
        damage_per_min: "Faible = tes Q ne touchent pas. Entraîne ton skillshot aim.",
        vision: "Wards défensifs côté flanc — E t'escape si tu as l'info.",
        deaths_per_game: "Max 3. Tu as E pour fuir — si tu meurs, mauvais cooldown management.",
        win_rate: "WR meilleur en late (30+ min, Manamune active)."
      },
      supportSynergy: ['enchanteuse', 'poke', 'yuumi', 'karma', 'lulu'],
      difficulty: 'medium'
    },

    Tristana: {
      archetype: 'hypercarry',
      subArchetypes: ['siege'],
      tags: [
        { label: 'Carry late', type: 'strength' },
        { label: 'Siege tour (E)', type: 'strength' },
        { label: 'Reset W kill', type: 'strength' },
        { label: 'Faible early', type: 'caution' },
        { label: 'Besoin protect', type: 'caution' },
      ],
      winCondition: "Farmer pour les objets, utiliser la portée croissante pour dominer le late, détruire les tours avec E. Meilleure carry en late après 3 objets.",
      powerSpike: "Niveau 7 (portée 575), 2 objets, niveau 13. Exponentiel par niveau.",
      keyMistakes: [
        "W pour chase au lieu de garder l'escape",
        "Ne pas reset W après un kill",
        "Oublier E sur une tour pour le push",
        "Rester à portée réduite (early) sans prendre de distance"
      ],
      axisAdvice: {
        cs_per_min: "8+ cs/min impératif. Besoin de 3 objets pour dominer.",
        kda: "KDA >2.5. Fragile en early, rare de mourir en late avec 3 objets.",
        lane_wr_15: "Moyen acceptable. Tortue : survive l'early, domine le late.",
        damage_per_min: "Faible en mid-game = E pas assez exploité. Combo E-auto-W reset.",
        vision: "Wards flancs — cible prioritaire des assassins jusqu'à portée max.",
        deaths_per_game: "Max 3. W reset = escape en early.",
        win_rate: "WR faible = tu perds avant 25 min. Joue avec un support engage protecteur."
      },
      supportSynergy: ['enchanteuse', 'bouclier', 'lulu', 'nami', 'soraka'],
      difficulty: 'low'
    },

    Corki: {
      archetype: 'siege',
      subArchetypes: ['poke'],
      tags: [
        { label: 'Poke magique', type: 'strength' },
        { label: 'Siege fort', type: 'strength' },
        { label: 'Roam mid-game', type: 'strength' },
        { label: 'Faible very late', type: 'caution' },
        { label: 'Package-dépendant', type: 'caution' },
      ],
      winCondition: "Deliver packages (R) pour poke avant les fights, gagner les sieges, win le mid-game avec le poke magique. Un siège mobile, pas un hypercarry.",
      powerSpike: "Premier package (7 min), Trinity, niveau 11. Fort mid-game, tombe en very late.",
      keyMistakes: [
        "Ne pas utiliser les packages (R) pour poke",
        "Oublier de récupérer le package au spawn",
        "Jouer Corki comme un ADC traditionnel",
        "Vendre Trinity pour des items AD purs"
      ],
      axisAdvice: {
        cs_per_min: "7+ cs/min. Meilleur en roam qu'en farm mais ne néglige pas les waves.",
        kda: "KDA >3 grâce à W. <2 = mauvais trades ou roam sans vision.",
        lane_wr_15: "Correct avec bon support. Préfère le roam au kill en lane.",
        damage_per_min: "Faible = packages pas exploités. Damage magique de R énorme en poke.",
        vision: "Ward les accès mid pour sécuriser les rotations.",
        deaths_per_game: "Max 3. W est ton escape.",
        win_rate: "WR faible en late : Corki tombe vs hypercarries. Force la win avant 30 min."
      },
      supportSynergy: ['poke', 'engage_dur', 'zone_control', 'zyra', 'seraphine'],
      difficulty: 'medium'
    },

    Vayne: {
      archetype: 'hypercarry',
      subArchetypes: ['skirmisher'],
      tags: [
        { label: 'Contre les tanks', type: 'strength' },
        { label: '1v1 monstre', type: 'strength' },
        { label: 'Scale infini', type: 'strength' },
        { label: 'Early misérable', type: 'caution' },
        { label: 'Besoin Lulu/Yuumi', type: 'caution' },
      ],
      winCondition: "Survivre l'early (ta pire phase), atteindre 3 objets, 1v1 tout le monde grâce au W (true damage). Tu contre les tanks.",
      powerSpike: "3 objets (Kraken + Guinsoo + Wit's End). Niveau 11. Très tardif.",
      keyMistakes: [
        "Aller sur les ennemis en early sans objets",
        "Oublier de coller aux murs (E) pour le stun",
        "R proactif au lieu de réactif",
        "Sous-estimer la fragilité early (niveau 1-5)"
      ],
      axisAdvice: {
        cs_per_min: "8+ cs/min impératif — besoin de 3 objets. Ne meurs jamais pour du farm.",
        kda: "KDA >3 impératif en early. Plus agressif en late avec 3 objets.",
        lane_wr_15: "<45% acceptable. L'objectif est de survivre, pas de gagner la lane.",
        damage_per_min: "Faible en late = pas assez de stacks W (3) sur les cibles. Focus les tanks.",
        vision: "Wards défensifs côté jungle ennemie — cible prioritaire des junglers.",
        deaths_per_game: "Max 3 total. Chaque mort early retarde tes objets.",
        win_rate: "WR faible = game finit trop tôt. Joue avec Lulu/Yuumi pour protéger l'early."
      },
      supportSynergy: ['enchanteuse', 'bouclier', 'lulu', 'yuumi', 'soraka'],
      difficulty: 'high'
    },

    Ashe: {
      archetype: 'utility',
      tags: [
        { label: 'Vision (E)', type: 'strength' },
        { label: 'Initiation R global', type: 'strength' },
        { label: 'Utility teamfight', type: 'strength' },
        { label: 'Aucune escape', type: 'caution' },
        { label: 'DPS moyen', type: 'caution' },
      ],
      winCondition: "Contrôler la carte avec Hawkshot (E), initier/interrompre avec R global, DPS constant avec autos ralentissantes. Tu gagnes par la vision et le contrôle.",
      powerSpike: "Niveau 6 (R global), Kraken + Runaan's. Fort tout le jeu, meilleur en teamfight.",
      keyMistakes: [
        "Ne pas utiliser Hawkshot (E) pour scout",
        "Garder R pour le kill au lieu d'initier/sauver",
        "Oublier d'activer Q avant les fights",
        "Se positionner en frontline — aucune escape"
      ],
      axisAdvice: {
        cs_per_min: "7.5+ cs/min. Efficace avec Q+auto. Pas d'excuse.",
        kda: "KDA >3 naturel. Aucune escape — mort = problème de positionnement/vision.",
        lane_wr_15: "Moyen acceptable. Pas un lane bully, impact en teamfight.",
        damage_per_min: "Faible = Q pas assez activé avant les fights.",
        vision: "Hawkshot (E) toutes les 60s, c'est gratuit. Scout baron/dragon/flancs.",
        deaths_per_game: "Max 2-3. Immobile — positionnement arrière strict.",
        win_rate: "WR faible : tu uses R pour setup des kills ou tu attends trop ?"
      },
      supportSynergy: ['engage_dur', 'cc_long', 'thresh', 'blitzcrank', 'leona'],
      difficulty: 'low'
    },

    MissFortune: {
      archetype: 'lane_bully',
      tags: [
        { label: 'Lane bully', type: 'strength' },
        { label: 'Burst R teamfight', type: 'strength' },
        { label: 'Pression early', type: 'strength' },
        { label: 'Perd si late', type: 'caution' },
        { label: 'Aucune escape', type: 'caution' },
      ],
      winCondition: "Dominer la lane avec la passive et Q (ricochet), accumuler des kills, win les teamfights avec R. Fenêtre : 0-25 min.",
      powerSpike: "Niveau 2 (Q+E), Kraken + Serylda's. Très fort early/mid, tombe en late.",
      keyMistakes: [
        "Bouger inutilement avant de tirer — perd le bonus passive (P)",
        "R sans setup (Amumu, Yasuo)",
        "Engager sans vision du jungler",
        "Rester en MF si game >30 min vs hypercarries"
      ],
      axisAdvice: {
        cs_per_min: "7+ cs/min. Reste immobile pour la passive — farm naturellement bien.",
        kda: "KDA >2.5. Fights early — quelques morts pour des kills acceptables.",
        lane_wr_15: ">55% attendu. MF doit dominer la lane, c'est sa raison d'être.",
        damage_per_min: "Faible = passive pas maintenue. Attends 2s après chaque mouvement.",
        vision: "Wards côté flanc — setup pour R (ennemis regroupés).",
        deaths_per_game: "Max 3. Aucune escape — positionnement critique.",
        win_rate: "WR faible en late : force la win avant 25 min."
      },
      supportSynergy: ['engage_dur', 'cc_long', 'amumu_supp', 'seraphine', 'leona'],
      difficulty: 'low'
    },

    Sivir: {
      archetype: 'utility',
      subArchetypes: ['siege'],
      tags: [
        { label: 'Wave clear roi', type: 'strength' },
        { label: 'Engage R équipe', type: 'strength' },
        { label: 'Spell shield (E)', type: 'strength' },
        { label: 'DPS moyen', type: 'caution' },
        { label: 'Besoin setup', type: 'caution' },
      ],
      winCondition: "Dominer le wave clear (Q+W), activer R pour les engages d'équipe, win les 5v5 par la mobilité collective. Tu gagnes par les objectifs rapides.",
      powerSpike: "Kraken + Runaan's. Niveau 11. Très fort en sieges et counter-engage.",
      keyMistakes: [
        "R pour fuir au lieu d'engage",
        "Stacks de W sur des minions en fight",
        "Ne pas utiliser E (Spell Shield) sur les CC",
        "Négliger le roam avec R"
      ],
      axisAdvice: {
        cs_per_min: "8+ cs/min naturel (Q+W). Une des meilleures pour le wave clear.",
        kda: "KDA >2.5. E bloque les gros CC — utilise-le bien.",
        lane_wr_15: "Moyen acceptable. Monte en teamfight.",
        damage_per_min: "Faible = W pas assez exploité en fight (bounce multi-cibles).",
        vision: "Ward pour sécuriser les rotations post-R.",
        deaths_per_game: "Max 3. Gère le cooldown de E.",
        win_rate: "WR faible : tu actives R pour setup les engages ou tu attends ?"
      },
      supportSynergy: ['engage_dur', 'zone_control', 'jarvan_supp', 'orianna_supp', 'zyra'],
      difficulty: 'low'
    },

    Samira: {
      archetype: 'skirmisher',
      tags: [
        { label: 'Clean teamfight R', type: 'strength' },
        { label: 'Combo stylé', type: 'strength' },
        { label: 'Fort mid-game', type: 'strength' },
        { label: 'Très technique', type: 'caution' },
        { label: 'Besoin engage', type: 'caution' },
      ],
      winCondition: "Accumuler les stacks de style (A→S) via le combo varié, déclencher R sur plusieurs ennemis groupés. Tu gagnes les fights rapprochés.",
      powerSpike: "Kraken + Shieldbow. Niveau 6 (R). Très fort en mid-game teamfight.",
      keyMistakes: [
        "R avec moins de 6 stacks — gaspillage total",
        "Ne pas bloquer les projectiles avec W au bon moment",
        "Rester backline — Samira est une melee carry",
        "Engager sans que l'équipe ait initié"
      ],
      axisAdvice: {
        cs_per_min: "7+ cs/min. Farm correct avec Q. Préfère les fights au farm pur.",
        kda: "KDA 2-3 (risques pour les stacks). >5 morts = dive trop tôt.",
        lane_wr_15: ">50% avec engage support. Faible = ton support ne set pas les all-ins.",
        damage_per_min: "Faible = R sans assez de stacks ou sur 1 seule cible.",
        vision: "Ward les flancs — vulnérable pendant le cast de R.",
        deaths_per_game: "Max 4. Fragile entre deux R — défensif après chaque R.",
        win_rate: "WR faible : R sur des groupes de 3+ ? Sinon attends le setup."
      },
      supportSynergy: ['engage_dur', 'cc_zone', 'amumu_supp', 'leona', 'rakan'],
      difficulty: 'high'
    },

    Xayah: {
      archetype: 'skirmisher',
      subArchetypes: ['utility'],
      tags: [
        { label: 'Contre engage', type: 'strength' },
        { label: 'R intouchable', type: 'strength' },
        { label: 'Synergie Rakan', type: 'strength' },
        { label: 'Faible sans Rakan', type: 'caution' },
        { label: 'Setup plumes', type: 'caution' },
      ],
      winCondition: "Accumuler des plumes (autos + Q), les rappeler avec E pour root, R pour être intouchable pendant les gros CC. Tu contres les compos engage.",
      powerSpike: "Essence Reaver + IE. Niveau 6 (R). Fort vs compos CC lourdes.",
      keyMistakes: [
        "Pas assez de plumes avant E",
        "R trop tôt au lieu d'attendre le burst ennemi",
        "Mauvais positionnement des plumes",
        "Ne pas profiter de la synergie Rakan"
      ],
      axisAdvice: {
        cs_per_min: "7.5+ cs/min. Farm bien avec Q. Focus l'accumulation de plumes.",
        kda: "KDA >3. R te rend intouchable — mort = R mal timé.",
        lane_wr_15: "Fort avec Rakan, moyen seule. Adapte selon le support.",
        damage_per_min: "Faible = pas assez de plumes placées. Chaque auto compte.",
        vision: "Wards flancs — besoin de temps pour placer les plumes.",
        deaths_per_game: "Max 3. R = escape ultime, garde-le.",
        win_rate: "WR faible : joues-tu avec Rakan ? Synergie massive en ranked."
      },
      supportSynergy: ['rakan', 'engage_dur', 'enchanteuse', 'thresh'],
      difficulty: 'medium'
    },

    Senna: {
      archetype: 'utility',
      tags: [
        { label: 'Heal/shield équipe', type: 'strength' },
        { label: 'Scale infini (âmes)', type: 'strength' },
        { label: 'R global', type: 'strength' },
        { label: 'Early faible', type: 'caution' },
        { label: 'Stack-dépendant', type: 'caution' },
      ],
      winCondition: "Accumuler des âmes (autos + morts ennemies), heal/shield l'équipe, win les teamfights avec R global. Support-carry hybride.",
      powerSpike: "50 stacks (portée +), Kraken, 100 stacks. Exponentiel avec le temps.",
      keyMistakes: [
        "Oublier de last-hit pour les âmes",
        "R pour du damage au lieu de bouclier/sauvegarde",
        "Jouer ADC sans support engage protecteur",
        "Ne pas profiter de la portée croissante"
      ],
      axisAdvice: {
        cs_per_min: "5+ cs/min mais préfère kills/âmes. Ne sacrifice pas les âmes pour du CS.",
        kda: "KDA >3. Kit défensif — mort = positionnement.",
        lane_wr_15: "Moyen. Monte avec les stacks — early = phase faible.",
        damage_per_min: "Faible après 20 min = peu d'âmes. Farm-les plus agressivement.",
        vision: "Wards défensifs — chaque mort coûte des stacks de progression.",
        deaths_per_game: "Max 3. Cible prioritaire.",
        win_rate: "WR faible : combien d'âmes à 20 min ? <40 = pas assez."
      },
      supportSynergy: ['engage_dur', 'cc_long', 'nautilus', 'thresh', 'blitzcrank'],
      difficulty: 'medium'
    },

    Aphelios: {
      archetype: 'hypercarry',
      tags: [
        { label: '5 armes flexibles', type: 'strength' },
        { label: 'DPS late massif', type: 'strength' },
        { label: 'Adaptable', type: 'strength' },
        { label: 'Très technique', type: 'caution' },
        { label: 'Aucune escape', type: 'caution' },
      ],
      winCondition: "Maîtriser la rotation des 5 armes pour adapter ta win condition. Fort en late avec Infernum (AoE) et Crescendum (DPS).",
      powerSpike: "2 objets, niveau 11. Dépend des armes actives au moment du fight.",
      keyMistakes: [
        "Ne pas adapter le positionnement selon l'arme",
        "R sans arme AoE (Infernum) — perd 50% du potentiel",
        "Oublier Calibrum (sniper) pour les engages",
        "Mal gérer la rotation des armes"
      ],
      axisAdvice: {
        cs_per_min: "8+ cs/min. Besoin de gold. Chaque arme farm différemment.",
        kda: "KDA >2.5. Fragile, aucune escape — positionnement strict.",
        lane_wr_15: "Variable selon les armes. Gravitum (root) = opportunités de kill.",
        damage_per_min: "Faible = quelle arme pendant les fights ? Crescendum/Infernum = DPS.",
        vision: "Wards côté jungler ennemi — cible prioritaire absolue.",
        deaths_per_game: "Max 3. Aucune mobilité.",
        win_rate: "WR faible (10+ games) : maîtrises-tu la rotation des 5 armes ?"
      },
      supportSynergy: ['enchanteuse', 'bouclier', 'lulu', 'soraka', 'yuumi'],
      difficulty: 'high'
    },

    Zeri: {
      archetype: 'skirmisher',
      subArchetypes: ['hypercarry'],
      tags: [
        { label: 'Mobilité infinie', type: 'strength' },
        { label: 'Scale fort', type: 'strength' },
        { label: 'AoE Q late', type: 'strength' },
        { label: 'Besoin shields', type: 'caution' },
        { label: 'Très technique', type: 'caution' },
      ],
      winCondition: "Accumuler des stacks de vitesse (passive), devenir ingérable en late grâce à la mobilité et au Q AoE. Tu contres les compos qui ne peuvent pas te toucher.",
      powerSpike: "Kraken + Runaan's + Trinity. Niveau 11. Fort avec boucliers alliés.",
      keyMistakes: [
        "Ne pas bouger pendant Q",
        "Engager sans stacks de vitesse",
        "Ne pas profiter des boucliers alliés (charge de vitesse)",
        "Jouer statique"
      ],
      axisAdvice: {
        cs_per_min: "7.5+ cs/min. Q excellent pour farm sous tour.",
        kda: "KDA >2.5. E donne de la mobilité — mort = mauvais engage ou flanc.",
        lane_wr_15: "Correct avec support shield, moyen seule.",
        damage_per_min: "Faible = pas de stacks de vitesse. Cherche les fights de groupe.",
        vision: "Wards flancs — E t'escape si tu as l'info.",
        deaths_per_game: "Max 3. En mouvement difficile à tuer.",
        win_rate: "WR faible : as-tu un support shield ? Lulu/Nami/Orianna quasi-obligatoires."
      },
      supportSynergy: ['bouclier', 'enchanteuse', 'lulu', 'orianna_supp', 'nami'],
      difficulty: 'high'
    },

    Twitch: {
      archetype: 'hypercarry',
      tags: [
        { label: '1v5 avec R', type: 'strength' },
        { label: 'Effet surprise', type: 'strength' },
        { label: 'Scale fort', type: 'strength' },
        { label: 'Mauvais en lane', type: 'caution' },
        { label: 'Aucune escape', type: 'caution' },
      ],
      winCondition: "Farmer invisible (Q), se placer derrière l'équipe adverse, déclencher R (Spray and Pray) sur une ligne. L'effet de surprise est ta seule arme.",
      powerSpike: "Kraken + Runaan's. Niveau 6 (Q amélioré). Fort en late après 3 objets.",
      keyMistakes: [
        "Engager trop tôt avant l'initiation de l'équipe",
        "R en 1v1 au lieu de 1v5",
        "Pas de stacks de venin avant E (Contaminate)",
        "Se positionner côté tank au lieu de flanc"
      ],
      axisAdvice: {
        cs_per_min: "7.5+ cs/min. Farm standard, pas de mécanisme spécial.",
        kda: "KDA >3. Invisible avant chaque fight — mort = découvert trop tôt.",
        lane_wr_15: "Faible acceptable. Mauvais en lane — survie et farm.",
        damage_per_min: "Faible = R sur pas assez de cibles. Flanc derrière l'ennemi.",
        vision: "Wards pour connaître les positions avant les flancs.",
        deaths_per_game: "Max 3. Aucune escape hors Q — découvert = mort.",
        win_rate: "WR faible : game finit avant le 3ème objet ? Twitch a besoin de temps."
      },
      supportSynergy: ['enchanteuse', 'bouclier', 'lulu', 'soraka', 'yuumi'],
      difficulty: 'medium'
    },

    Kalista: {
      archetype: 'lane_bully',
      subArchetypes: ['skirmisher'],
      tags: [
        { label: 'Domine early', type: 'strength' },
        { label: 'Mobilité (hop)', type: 'strength' },
        { label: 'Combo R support', type: 'strength' },
        { label: 'Faible en late', type: 'caution' },
        { label: 'Besoin engage', type: 'caution' },
      ],
      winCondition: "Dominer l'early avec le support (R = Fate's Call combo), snowball les premiers kills, et transformer le lead avant que ton scaling faiblisse en late.",
      powerSpike: "Niveau 1-6 avec engage support, Kraken/Manamune. Très fort early, faiblit en late.",
      keyMistakes: [
        "Ne pas bouger entre chaque auto (mécanique de hop obligatoire)",
        "Mal utiliser R (Fate's Call) — outil d'engage ET de sauvetage du support",
        "Ne pas stacker les spears (E) avant de les détonner",
        "Jouer Kalista sans support engage — elle est inutilisable solo"
      ],
      axisAdvice: {
        cs_per_min: "7+ cs/min. Le hop rend le last-hit technique — entraîne le mouvement+auto.",
        kda: "KDA >2.5. Fragile mais mobile (hop). Mort = mauvais positionnement de hop.",
        lane_wr_15: ">55% impératif. Kalista DOIT dominer la lane, son scaling est mauvais.",
        damage_per_min: "Faible = spears (E) pas assez stackés avant détonation. Stack puis E.",
        vision: "Ward côté jungler — Kalista punie sévèrement les ganks ratés avec le hop.",
        deaths_per_game: "Max 3. Le hop est ton escape — mort = mauvais usage du mouvement.",
        win_rate: "WR faible : joues-tu avec un engage support (Thresh, Naut, Leona) ? Obligatoire."
      },
      supportSynergy: ['engage_dur', 'cc_long', 'thresh', 'nautilus', 'leona'],
      difficulty: 'high'
    },

    "Kog'Maw": {
      archetype: 'hypercarry',
      tags: [
        { label: 'Tourelle ambulante', type: 'strength' },
        { label: 'DPS late absurde', type: 'strength' },
        { label: 'Dégâts post-mortem', type: 'strength' },
        { label: 'Zéro mobilité', type: 'caution' },
        { label: 'Besoin peel total', type: 'caution' },
      ],
      winCondition: "Survivre l'early (le plus immobile du jeu), atteindre 2-3 objets, et devenir une tourelle ambulante avec W (portée + attack speed massif). Tu as besoin de peel total.",
      powerSpike: "2 objets (Guinsoo + Runaan's/Rageblade). Niveau 11. Le plus tardif des hypercarries.",
      keyMistakes: [
        "Activer W trop tôt dans un fight (révèle ta fragilité)",
        "Se positionner sans peel — Kog'Maw mort = team sans DPS",
        "Oublier le passif (explose à la mort = dégâts post-mortem)",
        "Aller agressif en early — Kog'Maw n'a aucune mobilité"
      ],
      axisAdvice: {
        cs_per_min: "8+ cs/min impératif. Le scaling le plus dépendant des objets du jeu.",
        kda: "KDA >2.5. Tu vas mourir (pas d'escape) mais ton W doit avoir fait le travail avant.",
        lane_wr_15: "<45% acceptable. Survie pure — l'objectif est d'atteindre le late.",
        damage_per_min: "Si faible : tu n'actives pas W assez longtemps ou tu meurs avant. W = ton seul DPS.",
        vision: "Wards défensifs maximaux — tu es la cible #1 et tu ne peux pas fuir.",
        deaths_per_game: "3-4 acceptable SI ton W a fait des dégâts massifs avant. Mort sans damage = inutile.",
        win_rate: "WR faible : as-tu un support peel (Lulu, Janna, Braum) ? Vital pour Kog'Maw."
      },
      supportSynergy: ['enchanteuse', 'bouclier', 'lulu', 'janna', 'braum'],
      difficulty: 'high'
    },

    Nilah: {
      archetype: 'skirmisher',
      tags: [
        { label: 'Heal partagé (P)', type: 'strength' },
        { label: 'Clean teamfight R', type: 'strength' },
        { label: 'Esquive W', type: 'strength' },
        { label: 'Melee risquée', type: 'caution' },
        { label: 'Besoin engage', type: 'caution' },
      ],
      winCondition: "Coller au carry ennemi en teamfight, profiter du heal partagé (passive) et de l'esquive de W, et clean les fights groupés avec R. Une melee carry full teamfight.",
      powerSpike: "Kraken + Shieldbow. Niveau 6 (R). Très fort en teamfight groupé mid-game.",
      keyMistakes: [
        "Utiliser W (esquive) au mauvais moment — c'est ton seul outil défensif",
        "Engager seule sans front line",
        "Ne pas profiter du partage d'XP (lane solo possible)",
        "R sur 1 seule cible au lieu d'un groupe"
      ],
      axisAdvice: {
        cs_per_min: "7+ cs/min. Q a une portée correcte pour le farm. Reste focus lane.",
        kda: "KDA 2-3. Melee donc risquée — W esquive bien timé réduit les morts.",
        lane_wr_15: ">50% avec engage. Le heal partagé (P) renforce ton support.",
        damage_per_min: "Faible = R pas sur des groupes ou tu restes backline. Nilah doit plonger.",
        vision: "Wards flancs — vulnérable pendant l'engage.",
        deaths_per_game: "Max 4. W est ton seul escape — gère son cooldown.",
        win_rate: "WR faible : R sur des groupes de 3+ ? Nilah est une carry de teamfight groupé."
      },
      supportSynergy: ['engage_dur', 'cc_zone', 'leona', 'nautilus', 'rell'],
      difficulty: 'high'
    },

    Varus: {
      archetype: 'poke',
      subArchetypes: ['lane_bully', 'utility'],
      tags: [
        { label: 'Build flexible', type: 'strength' },
        { label: 'Pick R chaîné', type: 'strength' },
        { label: 'Poke Q', type: 'strength' },
        { label: 'Aucune escape', type: 'caution' },
        { label: 'Build-dépendant', type: 'caution' },
      ],
      winCondition: "Adapter le build à la situation : Lethality (poke/burst), On-hit (DPS sustained), ou AP (mage). Poke avec Q chargé, initier les fights avec R (root en chaîne).",
      powerSpike: "Premier objet selon le build. Niveau 6 (R) = pick potential. Build-dépendant.",
      keyMistakes: [
        "Mal choisir le build selon la composition adverse",
        "Charger Q au mauvais moment (immobile = vulnérable)",
        "R sur une cible isolée au lieu d'un groupe (le root chaîne)",
        "Ne pas stacker la passive (W) sur le bon ennemi avant le burst"
      ],
      axisAdvice: {
        cs_per_min: "7+ cs/min. Q chargé est excellent pour le wave clear et le farm à distance.",
        kda: "KDA >3 en lethality, >2.5 en on-hit. Aucune mobilité — positionnement strict.",
        lane_wr_15: "50-55%. Varus lethality est un lane bully, on-hit est plus scaling.",
        damage_per_min: "Faible = Q pas assez chargé ou mauvais build vs la compo adverse.",
        vision: "Wards — aucune escape, tu dois anticiper les ganks.",
        deaths_per_game: "Max 3. Immobile — R est ton seul outil de désengagement (root).",
        win_rate: "WR faible : adaptes-tu le build (Leth/On-hit/AP) selon l'adversaire ?"
      },
      supportSynergy: ['engage_dur', 'cc_long', 'poke', 'thresh', 'leona'],
      difficulty: 'medium'
    },

    Ziggs: {
      archetype: 'siege',
      subArchetypes: ['poke'],
      tags: [
        { label: 'Siege tour roi', type: 'strength' },
        { label: 'Poke longue portée', type: 'strength' },
        { label: 'R global objectif', type: 'strength' },
        { label: 'Fragile immobile', type: 'caution' },
        { label: 'Skillshot pur', type: 'caution' },
      ],
      winCondition: "Détruire les tourelles plus vite que n'importe qui (W = bonus damage tour), poke les ennemis de très loin avec Q, contrôler les objectifs avec R global. Un mage botlane.",
      powerSpike: "Premier objet AP (Liandry/Luden). Niveau 6 (R). Fort en siège et contrôle d'objectif.",
      keyMistakes: [
        "Ne pas utiliser W sur les tours (bonus damage de tourelle massif)",
        "Q sans anticiper le mouvement ennemi (skillshot)",
        "R gaspillé en wave clear au lieu de finisher/objectif",
        "Jouer trop en avant — Ziggs est fragile et immobile"
      ],
      axisAdvice: {
        cs_per_min: "7+ cs/min. Q wave clear excellent — pas d'excuse pour rater du farm.",
        kda: "KDA >3. Aucune escape hors W (self-knockback) — positionnement arrière.",
        lane_wr_15: "55%+ en poke. Ziggs harasse fort avec Q en lane.",
        damage_per_min: "Faible = Q raté ou pas assez de poke. Le damage AP de Ziggs est constant.",
        vision: "Wards — tu es immobile, tu dois voir les engages venir.",
        deaths_per_game: "Max 3. W (Satchel) est ton seul escape — garde-le.",
        win_rate: "WR faible : utilises-tu W sur les tours ? Le siège est ta win condition unique."
      },
      supportSynergy: ['engage_dur', 'cc_zone', 'zone_control', 'leona', 'nautilus'],
      difficulty: 'medium'
    },

    Seraphine: {
      archetype: 'utility',
      subArchetypes: ['poke', 'siege'],
      tags: [
        { label: 'Heal/shield/CC', type: 'strength' },
        { label: 'R charm chaîné', type: 'strength' },
        { label: 'Scale fort', type: 'strength' },
        { label: 'Early faible', type: 'caution' },
        { label: 'Immobile', type: 'caution' },
      ],
      winCondition: "Apporter heal/shield/CC à l'équipe (kit de support porté en ADC), poke avec Q, et win les teamfights avec R (charm en chaîne). Une carry-support hybride scaling.",
      powerSpike: "2 objets AP (Luden/Seraph). Niveau 6 (R). Forte en teamfight groupé late.",
      keyMistakes: [
        "R sans setup (charm chaîné nécessite des ennemis groupés)",
        "Jouer trop agressif — Seraphine est immobile et fragile",
        "Ne pas profiter du double cast de la passive (chaque 3ème sort)",
        "Négliger le wave clear avec W/E en mid-game"
      ],
      axisAdvice: {
        cs_per_min: "6+ cs/min. Plus utility que farm pur mais ne néglige pas les waves.",
        kda: "KDA >3. Kit défensif (heal/shield) — mort = positionnement.",
        lane_wr_15: "Moyen. Seraphine scale — l'early n'est pas sa phase forte.",
        damage_per_min: "Faible = Q pas assez touché. La passive double-cast amplifie le poke.",
        vision: "Wards défensifs — aucune escape, tu dois anticiper.",
        deaths_per_game: "Max 3. Immobile — positionnement arrière strict.",
        win_rate: "WR faible : R sur des groupes de 3+ (charm chaîné) ? C'est ton moment fort."
      },
      supportSynergy: ['engage_dur', 'cc_zone', 'leona', 'nautilus', 'amumu_supp'],
      difficulty: 'medium'
    },

    Smolder: {
      archetype: 'hypercarry',
      subArchetypes: ['siege'],
      tags: [
        { label: 'Exécution AoE late', type: 'strength' },
        { label: 'Scale infini', type: 'strength' },
        { label: 'Mobilité E', type: 'strength' },
        { label: 'Early misérable', type: 'caution' },
        { label: 'Stack-dépendant', type: 'caution' },
      ],
      winCondition: "Stacker Q (à 225 stacks, Q devient une exécution AoE massive), survivre l'early, et devenir une menace late game qui clear les fights. Le scaling le plus stack-dépendant.",
      powerSpike: "Q à 125 stacks (AoE), puis 225 stacks (exécution). 2-3 objets. Très tardif.",
      keyMistakes: [
        "Rater des Q (chaque Q raté = stack perdu = scaling retardé)",
        "Jouer agressif en early sans stacks",
        "Ne pas utiliser E (escape + survol) pour le positionnement",
        "Oublier que le early Smolder est l'un des plus faibles du jeu"
      ],
      axisAdvice: {
        cs_per_min: "7.5+ cs/min ET stacker Q sur les minions/champions. Le double objectif est crucial.",
        kda: "KDA >2.5. E donne de la mobilité — mort = E mal utilisé ou agression early.",
        lane_wr_15: "<45% acceptable. Smolder early est faible — survie et stack.",
        damage_per_min: "Faible en late = pas assez de stacks Q. Vérifie ton compteur de stacks à 20 min.",
        vision: "Wards défensifs — fragile jusqu'au full stack.",
        deaths_per_game: "Max 3. E (survol de terrain) est ton escape — garde-le.",
        win_rate: "WR faible : combien de stacks Q à 20 min ? <125 = tu ne stackes pas assez."
      },
      supportSynergy: ['enchanteuse', 'bouclier', 'lulu', 'soraka', 'janna'],
      difficulty: 'medium'
    },

  }
};

export function getChampionArchetype(championName, role = 'ADC') {
  const roleData = CHAMPION_ARCHETYPES[role];
  if (!roleData) return null;
  return roleData[championName] || null;
}

export function getAxisCoachingAdvice(axisKey, axisScore, champion, role = 'ADC') {
  const archetype = getChampionArchetype(champion, role);
  const championAdvice = archetype?.axisAdvice?.[axisKey] || null;
  const priority = axisScore < 30 ? 'high' : axisScore < 55 ? 'medium' : 'low';
  return {
    championAdvice,
    priority,
    archetype: archetype?.archetype || null,
    subArchetypes: archetype?.subArchetypes || [],
  };
}

/**
 * Génère la liste complète de tags pour un champion :
 *   - tags statiques (strength/caution) depuis la fiche
 *   - tags warning dynamiques depuis les stats réelles du joueur
 *
 * @param {string} champion
 * @param {object} stats — { games, wins, avgDeaths, avgCs, laneWr } sur ce champion
 * @param {string} role
 * @returns {{ label: string, type: 'strength'|'caution'|'warning' }[]}
 */
export function generateChampionTags(champion, stats, role = 'ADC') {
  const arch = getChampionArchetype(champion, role);
  if (!arch) return [];

  const tags = [...(arch.tags || [])];

  // Pas assez de games pour des warnings statistiquement fiables
  if (!stats || stats.games < 5) return tags;

  const wr = Math.round((stats.wins / stats.games) * 100);

  // Warning : WR faible
  if (wr <= 40) {
    tags.push({ label: `WR ${wr}% (${stats.games}g)`, type: 'warning' });
  }

  // Warning : trop de morts (seuil selon archétype)
  // Hypercarry/poke = seuil bas (doivent survivre), bully/skirmisher = seuil + tolérant
  const deathThreshold =
    ['hypercarry', 'poke', 'utility'].includes(arch.archetype) ? 4 : 5;
  if (stats.avgDeaths != null && stats.avgDeaths > deathThreshold) {
    tags.push({ label: `${stats.avgDeaths.toFixed(1)} morts/game`, type: 'warning' });
  }

  // Warning : CS/min faible pour les archétypes qui en dépendent
  if (
    ['hypercarry', 'poke', 'siege'].includes(arch.archetype) &&
    stats.avgCs != null && stats.avgCs < 6.5
  ) {
    tags.push({ label: `${stats.avgCs.toFixed(1)} cs/min faible`, type: 'warning' });
  }

  // Warning : lane perdue souvent pour les lane bully
  if (
    arch.archetype === 'lane_bully' &&
    stats.laneWr != null && stats.laneWr < 45
  ) {
    tags.push({ label: `Lane WR ${Math.round(stats.laneWr)}%`, type: 'warning' });
  }

  return tags;
}

export function analyzeChampionPool(pool, games, role = 'ADC') {
  const alerts = [];
  const roleData = CHAMPION_ARCHETYPES[role];
  if (!roleData || !Array.isArray(pool)) return alerts;

  const activePool = pool.filter(c => c.status !== 'sidelined');

  if (activePool.length > 5) {
    alerts.push({
      type: 'warning',
      title: 'Pool trop large',
      detail: `${activePool.length} champions actifs. Au-delà de 5, tu dilues ta maîtrise. Considère sideliner les moins joués.`,
    });
  }

  const cutoff = Date.now() - MONTH_MS;
  const recentGames = Array.isArray(games)
    ? games.filter(g => new Date(g.timestamp) > cutoff)
    : [];

  const playedCounts = {};
  recentGames.forEach(g => {
    if (!g.champion) return;
    playedCounts[g.champion] = (playedCounts[g.champion] || 0) + 1;
  });

  const poolNames = new Set(pool.map(c => c.name));
  Object.entries(playedCounts).forEach(([champ, count]) => {
    if (count >= 4 && !poolNames.has(champ)) {
      alerts.push({
        type: 'info',
        title: `${champ} hors pool`,
        detail: `Tu as joué ${champ} ${count} fois ce mois sans l'avoir dans ton pool déclaré.`,
        action: { type: 'add_to_pool', champion: champ }
      });
    }
  });

  Object.entries(playedCounts).forEach(([champ, count]) => {
    if (count < 5 || !poolNames.has(champ)) return;
    const wins = recentGames.filter(g => g.champion === champ && g.result === 'W').length;
    const wr = Math.round((wins / count) * 100);
    const inActivePool = pool.find(c => c.name === champ && c.status !== 'sidelined');
    if (wr <= 33 && inActivePool) {
      alerts.push({
        type: 'danger',
        title: `${champ} en difficulté`,
        detail: `${wr}% WR sur ${count} games. Considère de le sideliner le temps de retravailler.`,
        action: { type: 'sideline_champion', champion: champ }
      });
    }
  });

  return alerts;
}
